/**
 * B5: HR Verification Page - 4-Step Flow
 * 
 * Steps:
 * 1. Verify Identity - Confirm HR authority
 * 2. Employment Facts - Verify/amend job title, dates, departure type
 * 3. Rehire Eligibility - Select eligibility status with note
 * 4. Attestation - HR compliance checkbox and submit
 * 
 * Also includes: Decline path with reason
 * 
 * SEALED FIELDS: rehire_eligibility, rehire_note, internal_notes
 * These are visible only to the professional during their review, never to the manager.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, AlertCircle, CheckCircle, X, Loader2, Shield, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Text Area with Counter
const TextAreaWithCounter = ({ value, onChange, label, maxChars, placeholder, required, sealed }) => {
  const charCount = value ? value.length : 0;
  
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
        {label} {required && <span className="text-red-400">*</span>}
        {sealed && (
          <span className="flex items-center gap-1 text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">
            <Lock className="w-3 h-3" />
            Sealed
          </span>
        )}
      </label>
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent"
      />
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>{charCount} chars {maxChars && `/ ${maxChars} max`}</span>
        {sealed && <span className="text-amber-400">Only visible to the employee</span>}
      </div>
    </div>
  );
};

// Progress Rail
const ProgressRail = ({ currentStep, totalSteps, stepLabels }) => {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        {stepLabels.map((label, index) => (
          <div
            key={index}
            className={`flex flex-col items-center ${
              index <= currentStep ? 'text-coral-400' : 'text-gray-500'
            }`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                index < currentStep
                  ? 'bg-coral-500 text-white'
                  : index === currentStep
                  ? 'bg-coral-500/20 border-2 border-coral-500 text-coral-400'
                  : 'bg-gray-700 text-gray-500'
              }`}
            >
              {index < currentStep ? <CheckCircle className="w-5 h-5" /> : index + 1}
            </div>
            <span className="text-xs mt-1 hidden sm:block text-center">{label}</span>
          </div>
        ))}
      </div>
      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-coral-500 to-coral-400 transition-all duration-300"
          style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
        />
      </div>
    </div>
  );
};

// Decline Modal
const DeclineModal = ({ isOpen, onClose, onConfirm, loading }) => {
  const [reason, setReason] = useState('');
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-md w-full p-6 border border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-white">Decline Verification</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-400 mb-4">
          Please provide a reason for declining this verification request.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for declining (e.g., no records found, cannot verify)"
          rows={3}
          className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
        />
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Confirm Decline'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Rehire Eligibility Options
const REHIRE_OPTIONS = [
  { value: 'yes_without_reservation', label: 'Yes, without reservation', color: 'green' },
  { value: 'yes_with_right_role', label: 'Yes, with the right role', color: 'blue' },
  { value: 'no', label: 'No', color: 'red' },
  { value: 'policy_prevents', label: 'Our policy prevents answering this', color: 'gray' },
];

// Departure Type Options
const DEPARTURE_OPTIONS = [
  { value: 'resigned_voluntarily', label: 'Resigned voluntarily' },
  { value: 'laid_off', label: 'Laid off / Reduction in force' },
  { value: 'terminated_performance', label: 'Terminated for performance' },
  { value: 'terminated_conduct', label: 'Terminated for conduct' },
  { value: 'contract_ended', label: 'Contract ended' },
  { value: 'mutual_separation', label: 'Mutual separation' },
  { value: 'other', label: 'Other' },
];

export default function HRVerifyPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [prefill, setPrefill] = useState(null);
  const [recordId, setRecordId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    // Step 1: Identity
    hr_authority_confirmed: false,
    hr_department: '',
    
    // Step 2: Employment Facts
    job_title_confirmed: true,
    amended_job_title: '',
    start_date_confirmed: true,
    amended_start_date: '',
    end_date_confirmed: true,
    amended_end_date: '',
    departure_type: '',
    
    // Step 3: Rehire Eligibility (SEALED)
    rehire_eligibility: '',
    rehire_note: '',
    internal_notes: '',
    
    // Step 4: Attestation
    hr_attestation_accepted: false,
  });
  
  const stepLabels = ['Identity', 'Employment', 'Rehire', 'Submit'];
  
  useEffect(() => {
    fetchPrefill();
  }, [token]);
  
  const fetchPrefill = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dual-consent/hr/${token}`);
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Invalid or expired token');
      }
      const data = await response.json();
      setPrefill(data.prefill);
      setRecordId(data.record_id);
      setFormData(prev => ({
        ...prev,
        amended_job_title: data.prefill?.job_title || '',
        amended_start_date: data.prefill?.start_date || '',
        amended_end_date: data.prefill?.end_date || '',
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDecline = async (reason) => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/dual-consent/hr/${token}/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to decline');
      }
      setShowDeclineModal(false);
      setSuccess(true);
      setError('You have declined this verification request.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/dual-consent/hr/${token}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to submit verification');
      }
      
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.hr_authority_confirmed;
      case 1:
        return formData.departure_type;
      case 2:
        return formData.rehire_eligibility && 
               (formData.rehire_eligibility === 'policy_prevents' || formData.rehire_note.length > 0);
      case 3:
        return formData.hr_attestation_accepted;
      default:
        return false;
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-coral-500 animate-spin" />
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-xl max-w-md w-full p-8 text-center border border-gray-700">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Thank You!</h1>
          <p className="text-gray-400">
            Your verification has been submitted successfully. The employee will be notified.
          </p>
        </div>
      </div>
    );
  }
  
  if (error && !prefill) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-xl max-w-md w-full p-8 text-center border border-red-500/50">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Invalid Link</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-6 h-6 text-coral-400" />
            <h1 className="text-3xl font-bold text-white">HR Verification</h1>
          </div>
          <p className="text-gray-400">
            Verify employment for <span className="text-coral-400">{prefill?.employee_name || 'this employee'}</span>
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {prefill?.job_title} at {prefill?.employer_name}
          </p>
        </div>
        
        {/* Sealed Fields Notice */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-400">Sealed Information</h3>
              <p className="text-sm text-gray-400">
                Rehire eligibility and internal notes are sealed. They will only be visible to the 
                employee during their final review, never to the manager or external parties.
              </p>
            </div>
          </div>
        </div>
        
        {/* Progress Rail */}
        <ProgressRail currentStep={currentStep} totalSteps={4} stepLabels={stepLabels} />
        
        {/* Error Banner */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-400">{error}</p>
          </div>
        )}
        
        {/* Step Content */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 mb-6">
          {/* Step 1: Identity Verification */}
          {currentStep === 0 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-4">Verify Your Authority</h2>
              <p className="text-gray-400 mb-6">
                Please confirm you are authorized to verify employment records for {prefill?.employer_name}.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Your HR role/department
                </label>
                <select
                  value={formData.hr_department}
                  onChange={(e) => updateField('hr_department', e.target.value)}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-coral-500"
                >
                  <option value="">Select role</option>
                  <option value="hr_manager">HR Manager</option>
                  <option value="hr_director">HR Director</option>
                  <option value="hr_generalist">HR Generalist</option>
                  <option value="hr_coordinator">HR Coordinator</option>
                  <option value="people_ops">People Operations</option>
                  <option value="payroll">Payroll</option>
                  <option value="other">Other HR Role</option>
                </select>
              </div>
              
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.hr_authority_confirmed}
                  onChange={(e) => updateField('hr_authority_confirmed', e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-600 bg-gray-900 text-coral-500 focus:ring-coral-500"
                />
                <span className="text-gray-300">
                  I confirm that I am authorized by {prefill?.employer_name || 'this company'} to verify 
                  employment records and provide official HR information.
                </span>
              </label>
            </div>
          )}
          
          {/* Step 2: Employment Facts */}
          {currentStep === 1 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-4">Employment Facts</h2>
              <p className="text-gray-400 mb-6">
                Verify the official employment information below.
              </p>
              
              {/* Job Title */}
              <div className="bg-gray-900 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-500">Job Title (Employee Claimed)</p>
                <p className="text-white font-medium">{prefill?.job_title}</p>
              </div>
              
              <label className="flex items-center gap-3 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.job_title_confirmed}
                  onChange={(e) => updateField('job_title_confirmed', e.target.checked)}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-900 text-coral-500 focus:ring-coral-500"
                />
                <span className="text-gray-300">Official job title is correct</span>
              </label>
              
              {!formData.job_title_confirmed && (
                <input
                  type="text"
                  value={formData.amended_job_title}
                  onChange={(e) => updateField('amended_job_title', e.target.value)}
                  placeholder="Enter official job title per HR records"
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white mb-4 focus:outline-none focus:ring-2 focus:ring-coral-500"
                />
              )}
              
              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-900 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Start Date</p>
                  <p className="text-white font-medium">{prefill?.start_date}</p>
                </div>
                <div className="bg-gray-900 rounded-lg p-4">
                  <p className="text-sm text-gray-500">End Date</p>
                  <p className="text-white font-medium">{prefill?.end_date || 'Current'}</p>
                </div>
              </div>
              
              <label className="flex items-center gap-3 mb-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.start_date_confirmed && formData.end_date_confirmed}
                  onChange={(e) => {
                    updateField('start_date_confirmed', e.target.checked);
                    updateField('end_date_confirmed', e.target.checked);
                  }}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-900 text-coral-500 focus:ring-coral-500"
                />
                <span className="text-gray-300">Employment dates are correct</span>
              </label>
              
              {/* Departure Type */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Departure Type <span className="text-red-400">*</span>
                </label>
                <select
                  value={formData.departure_type}
                  onChange={(e) => updateField('departure_type', e.target.value)}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-coral-500"
                >
                  <option value="">Select departure type</option>
                  {DEPARTURE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          
          {/* Step 3: Rehire Eligibility (SEALED) */}
          {currentStep === 2 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-2 flex items-center gap-2">
                Rehire Eligibility
                <span className="flex items-center gap-1 text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded">
                  <Lock className="w-3 h-3" />
                  Sealed Section
                </span>
              </h2>
              <p className="text-gray-400 mb-6">
                This information is confidential and will only be visible to the employee during their final review.
              </p>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Would you rehire this employee? <span className="text-red-400">*</span>
                </label>
                <div className="space-y-2">
                  {REHIRE_OPTIONS.map(opt => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                        formData.rehire_eligibility === opt.value
                          ? opt.color === 'green' ? 'bg-green-500/10 border-green-500'
                            : opt.color === 'blue' ? 'bg-blue-500/10 border-blue-500'
                            : opt.color === 'red' ? 'bg-red-500/10 border-red-500'
                            : 'bg-gray-700 border-gray-500'
                          : 'bg-gray-900 border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="rehire_eligibility"
                        value={opt.value}
                        checked={formData.rehire_eligibility === opt.value}
                        onChange={(e) => updateField('rehire_eligibility', e.target.value)}
                        className="w-4 h-4 text-coral-500 focus:ring-coral-500"
                      />
                      <span className="text-white">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {formData.rehire_eligibility && formData.rehire_eligibility !== 'policy_prevents' && (
                <TextAreaWithCounter
                  value={formData.rehire_note}
                  onChange={(v) => updateField('rehire_note', v)}
                  label="Rehire Note"
                  placeholder="Provide context for this decision (required for definite answers)"
                  maxChars={500}
                  required
                  sealed
                />
              )}
              
              <TextAreaWithCounter
                value={formData.internal_notes}
                onChange={(v) => updateField('internal_notes', v)}
                label="Internal Notes (Optional)"
                placeholder="Any additional HR notes about this employee..."
                maxChars={1000}
                sealed
              />
            </div>
          )}
          
          {/* Step 4: Attestation */}
          {currentStep === 3 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-4">Attestation & Submit</h2>
              
              <div className="bg-gray-900 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-white mb-3">Verification Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Job Title:</span>
                    <span className="text-white">
                      {formData.job_title_confirmed ? prefill?.job_title : formData.amended_job_title}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Departure Type:</span>
                    <span className="text-white">
                      {DEPARTURE_OPTIONS.find(o => o.value === formData.departure_type)?.label}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Rehire Eligibility:</span>
                    <span className={
                      formData.rehire_eligibility === 'yes_without_reservation' ? 'text-green-400' :
                      formData.rehire_eligibility === 'yes_with_right_role' ? 'text-blue-400' :
                      formData.rehire_eligibility === 'no' ? 'text-red-400' : 'text-gray-400'
                    }>
                      {REHIRE_OPTIONS.find(o => o.value === formData.rehire_eligibility)?.label}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-blue-400 mb-2">HR Compliance Notice</h3>
                <p className="text-sm text-gray-400">
                  This verification must be based on official employment records maintained by the 
                  company. All information provided becomes part of a legally attestable employment 
                  verification record.
                </p>
              </div>
              
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.hr_attestation_accepted}
                  onChange={(e) => updateField('hr_attestation_accepted', e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-600 bg-gray-900 text-coral-500 focus:ring-coral-500"
                />
                <span className="text-gray-300">
                  I attest that this verification is accurate based on official employment records 
                  maintained by {prefill?.employer_name || 'this company'}. I understand this will 
                  become part of the employee&#39;s verified career record and cannot be modified after 
                  submission.
                </span>
              </label>
            </div>
          )}
        </div>
        
        {/* Navigation Buttons */}
        <div className="flex gap-4">
          {currentStep > 0 && (
            <button
              onClick={() => setCurrentStep(prev => prev - 1)}
              className="flex items-center gap-2 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>
          )}
          
          <button
            onClick={() => setShowDeclineModal(true)}
            className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-colors border border-red-600/50"
          >
            Decline
          </button>
          
          <div className="flex-1" />
          
          {currentStep < 3 ? (
            <button
              onClick={() => setCurrentStep(prev => prev + 1)}
              disabled={!canProceed()}
              className="flex items-center gap-2 px-6 py-3 bg-coral-500 text-white rounded-lg hover:bg-coral-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canProceed() || submitting}
              className="flex items-center gap-2 px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Submit Verification
                </>
              )}
            </button>
          )}
        </div>
      </div>
      
      <DeclineModal
        isOpen={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        onConfirm={handleDecline}
        loading={submitting}
      />
    </div>
  );
}
